import React, { ComponentType, useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { UserTag } from '../../../apps/professional-app/fa/UserTag'

import {
  CustomDropdownItemOption,
  DropDownItemOptionComponentProps,
  SelectDropdown,
  SelectDropdownRef,
  SelectOption,
} from '../../../components/SelectDropdown/SelectDropdown'
import { UserAvatar } from '../../../components/UserAvatar/UserAvatar'

import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetUsersQuery } from '../../../store/user/user'
import { useAppSelector } from '../../../store/hooks'

import { User } from '../../../models/User'

import { getInitials } from '../../utils'
import { hasClientRole } from './utils'

const zocksUserToNewEmailUserSelectOption = (user: User) => {
  const displayName = getUserDisplayName(user)
  return {
    id: user.id,
    label: `${displayName} <${user.email}>`,
  }
}

interface NewEmailUserSelectorProps {
  label: string
  placeholder?: string
  initialSelectedUser?: User | null
  onUserSelected?: (user: User | null) => void
  IconComponent?: ComponentType
  onlyClients?: boolean
  disabled?: boolean
  preventOpenOnMount?: boolean
}

export const NewEmailUserSelector: React.FC<NewEmailUserSelectorProps> = ({
  label,
  placeholder,
  initialSelectedUser = null,
  onUserSelected,
  IconComponent,
  onlyClients = false,
  disabled = false,
  preventOpenOnMount = false,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const dropdownRef = useRef<SelectDropdownRef>(null)

  const [searchInput, setSearchInput] = useState<string | undefined>()
  const [selectedUser, setSelectedUser] = useState<User | null>(initialSelectedUser)

  const { data: usersResponse } = useGetUsersQuery(
    { accountId, identityTypes: ['external'], name: searchInput, pageRequest: { size: 50, page: 0 } },
    { refetchOnMountOrArgChange: true },
  )

  useEffect(() => {
    if (preventOpenOnMount || !dropdownRef.current) {
      return
    }
    dropdownRef.current.focus()
  }, [preventOpenOnMount])

  const userOptions = useMemo(() => {
    if (!usersResponse) {
      return []
    }
    if (!onlyClients) {
      return usersResponse.data.map(zocksUserToNewEmailUserSelectOption)
    }
    return usersResponse.data.filter(hasClientRole).map(zocksUserToNewEmailUserSelectOption)
  }, [usersResponse, onlyClients])

  const handleSearchInputChange = async (newSearchInput: string) => {
    setSearchInput(newSearchInput)
  }

  const handleOptionSelected = (selectOption: SelectOption) => {
    if (!usersResponse) {
      return
    }
    const user = usersResponse.data.find((e) => e.id === selectOption.id)
    if (!user) {
      return
    }
    setSelectedUser(user)
    onUserSelected && onUserSelected(user)
  }

  const handleOptionDeselected = () => {
    setSelectedUser(null)
    onUserSelected && onUserSelected(null)

    if (!dropdownRef.current) {
      return
    }
    dropdownRef.current.focus()
  }

  const NewEmailUserSelectorIconComponent = () => {
    if (!selectedUser) {
      return null
    }
    const { name } = selectedUser
    return (
      <UserAvatar
        width='1.5rem'
        height='1.5rem'
        fontSize='0.75rem'
        className={`lead-extra-small zocks-text-white flex-shrink-0 ${hasClientRole(selectedUser) ? 'zocks-bg-color-primary' : 'zocks-bg-color-gray-900'}`}
        useInitials
        initialLetters={getInitials(name)}
      />
    )
  }

  const NewEmailUserSelectorDropdownItemOptionComponent: React.FC<DropDownItemOptionComponentProps> = ({ optionId, optionLabel, onClick, selected }) => {
    if (!usersResponse) {
      return null
    }

    const user = usersResponse.data.find((e) => e.id === optionId)

    return (
      <CustomDropdownItemOption role='button' onClick={selected ? () => {} : onClick} className='d-flex gap-2 align-items-center'>
        {optionLabel}
        {user && user.type ? <UserTag withoutStyle userType={user.type} /> : null}
      </CustomDropdownItemOption>
    )
  }

  return (
    <div className='d-flex align-items-center'>
      <Form.Label className='d-flex align-items-center zocks-gap-0.5 new-email-selector-label m-0 flex-shrink-0'>
        <span className='paragraph-medium'>{label}:</span>
        {IconComponent ? <IconComponent /> : null}
      </Form.Label>
      <SelectDropdown
        ref={dropdownRef}
        className='border-0'
        searchInputClassName='new-email-selector-search-input'
        options={userOptions}
        initialSelectedOption={selectedUser ? zocksUserToNewEmailUserSelectOption(selectedUser) : null}
        DropDownItemOptionComponent={NewEmailUserSelectorDropdownItemOptionComponent}
        IconComponent={NewEmailUserSelectorIconComponent}
        onSearchInputChange={handleSearchInputChange}
        onOptionSelected={handleOptionSelected}
        disabled={disabled}
        placeholder={placeholder}
        hideClearButton={true}
        hideArrows={true}
        searchable={true}
        inputAutoComplete={'off'}
        inputName={'newEmailUserSearch'}
      />
      {!disabled && selectedUser ? (
        <div className='new-email-selector-clear-icon-wrapper d-flex align-items-center justify-content-center flex-shrink-0'>
          <i className='bi bi-x-lg cursor-pointer' onClick={handleOptionDeselected} />
        </div>
      ) : null}
    </div>
  )
}
